import { useState } from 'react';

import type { CreateMcpConnectorInput } from '../types';

export interface Step1IdentityProps {
  value: Partial<CreateMcpConnectorInput>;
  onNext: (data: Pick<CreateMcpConnectorInput, 'slug' | 'displayName' | 'namespace'>) => void;
}

/**
 * Sprint A4 S1 PR 3 — Task 7. Step 1 do wizard: displayName + slug + namespace.
 *
 * slug auto-gerado a partir do displayName via slugify (lowercase + NFD strip
 * accents + non-alphanum → hyphen + trim). Marca `slugTouched` quando user
 * editar manualmente para parar de auto-sobrescrever.
 *
 * namespace default = slug (convenção do contrato canonical MCP). Operador
 * pode customizar se fornecedor expôs prefix diferente.
 */
export function McpConnectorStep1Identity({ value, onNext }: Step1IdentityProps): JSX.Element {
  const [displayName, setDisplayName] = useState(value.displayName ?? '');
  const [slug, setSlug] = useState(value.slug ?? '');
  const [namespace, setNamespace] = useState(value.namespace ?? '');
  const [slugTouched, setSlugTouched] = useState(false);

  function slugify(s: string): string {
    return s
      .toLowerCase()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');
  }

  function handleDisplayNameChange(v: string): void {
    setDisplayName(v);
    if (!slugTouched) {
      const auto = slugify(v);
      setSlug(auto);
      setNamespace(auto);
    }
  }

  const slugValid = /^[a-z][a-z0-9-]*$/.test(slug);
  const canProceed =
    displayName.length >= 2 && slug.length >= 2 && slugValid && namespace.length >= 2;

  return (
    <div className="flex flex-col gap-4">
      <div>
        <label htmlFor="step1-displayName" className="block text-sm font-medium text-text-primary">
          Nome visível *
        </label>
        <input
          id="step1-displayName"
          type="text"
          value={displayName}
          onChange={(e) => handleDisplayNameChange(e.target.value)}
          placeholder="Ex: ERP Aurora"
          className="mt-1 w-full rounded border border-border-subtle px-3 py-2 text-sm"
        />
        <p className="mt-1 text-xs text-text-secondary">
          Como será exibido na lista de conectores.
        </p>
      </div>

      <div>
        <label htmlFor="step1-slug" className="block text-sm font-medium text-text-primary">
          Identificador (slug) *
        </label>
        <input
          id="step1-slug"
          type="text"
          value={slug}
          onChange={(e) => {
            setSlug(e.target.value);
            setSlugTouched(true);
          }}
          className="mt-1 w-full rounded border border-border-subtle px-3 py-2 font-mono text-sm"
        />
        <p className="mt-1 text-xs text-text-secondary">
          kebab-case, único no tenant. Auto-gerado a partir do nome.
        </p>
        {!slugValid && slug.length > 0 && (
          <p className="mt-1 text-xs text-red-600">
            Slug deve começar com letra minúscula e usar apenas a-z, 0-9, -
          </p>
        )}
      </div>

      <div>
        <label htmlFor="step1-namespace" className="block text-sm font-medium text-text-primary">
          Namespace MCP *
        </label>
        <input
          id="step1-namespace"
          type="text"
          value={namespace}
          onChange={(e) => setNamespace(e.target.value)}
          className="mt-1 w-full rounded border border-border-subtle px-3 py-2 font-mono text-sm"
        />
        <p className="mt-1 text-xs text-text-secondary">
          Prefix usado nas tools (geralmente igual ao slug). Confirme com o fornecedor do MCP.
        </p>
      </div>

      <div className="mt-4 flex justify-end">
        <button
          type="button"
          onClick={() => onNext({ displayName, slug, namespace })}
          disabled={!canProceed}
          className="rounded bg-accent px-4 py-2 text-sm text-white hover:bg-accent-hover disabled:opacity-50"
        >
          Próximo →
        </button>
      </div>
    </div>
  );
}
